/*
SHARE3DCAM FAQ 通用组件
模块名称：SHARE FAQ Schema Interaction
文件位置：assets/share-faq-schema.js
添加日期：2026-06-23
添加人：SHARE3DCAM 独立站运营 / Yulin
最新修改日期：2026-06-23
版本号：v1.1.0

模块用途：
统一维护 SHARE FAQ Schema Section 的前台交互，包括 FAQ 展开收起、
PC / 移动端独立分页、默认展开项、窗口尺寸变化后的高度重算，
以及 Shopify 主题编辑器加载兼容。

当前完整功能：
1. 支持同一页面多个 SHARE FAQ Schema Section 独立运行。
2. 支持 FAQ 单项展开，默认同一时间只展开一个问题。
3. 支持 Desktop items per page 和 Mobile items per page 分开设置。
4. 设置为 0 时显示全部 FAQ，不显示分页。
5. 支持分页按钮自动生成 01 / 02 / 03。
6. 支持上一页 / 下一页切换。
7. 支持 Default open item 默认展开指定 FAQ。
8. 支持窗口 resize 后根据 PC / Mobile 状态重新分页。
9. 支持 Shopify Customize 中 section 动态加载后重新初始化。
10. 不依赖页面具体位置，不影响 Product、ATC、原生 FAQ 或其他主题 JS。

本次修改：
新增 PC / 移动端独立分页逻辑，并在窗口宽度变化时自动重新计算分页。

维护说明：
新增 Layout 时，优先复用 data-share-faq-* 属性；除非交互逻辑明显不同，否则不要新增独立 JS。
*/

(() => {
  const sectionSelector = '[data-share-faq-section]';
  const mobileMediaQuery = window.matchMedia('(max-width: 700px)');

  const closeItem = (item) => {
    const button = item.querySelector('[data-share-faq-button]');
    const answer = item.querySelector('[data-share-faq-answer]');

    item.classList.remove('is-open');
    button?.setAttribute('aria-expanded', 'false');

    if (answer) {
      answer.style.height = '0px';
    }
  };

  const openItem = (item) => {
    const button = item.querySelector('[data-share-faq-button]');
    const answer = item.querySelector('[data-share-faq-answer]');

    if (!button || !answer) {
      return;
    }

    item.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    answer.style.height = `${answer.scrollHeight}px`;
  };

  const getItemsPerPage = (pagination) => {
    if (!pagination) {
      return 0;
    }

    const desktopValue = Number(pagination.dataset.itemsPerPage || 0);
    const mobileValue = Number(
      pagination.dataset.mobileItemsPerPage || desktopValue || 0
    );

    const activeValue = mobileMediaQuery.matches ? mobileValue : desktopValue;

    return Number.isFinite(activeValue) ? Math.max(0, activeValue) : 0;
  };

  const initShareFaqSection = (section) => {
    if (!section) {
      return;
    }

    if (section.dataset.initialized === 'true') {
      section.dispatchEvent(new CustomEvent('shareFaq:refresh'));
      return;
    }

    section.dataset.initialized = 'true';

    const items = Array.from(
      section.querySelectorAll('[data-share-faq-item]')
    );

    const previousButton = section.querySelector('[data-share-faq-prev]');
    const nextButton = section.querySelector('[data-share-faq-next]');
    const pagesContainer = section.querySelector('[data-share-faq-pages]');
    const status = section.querySelector('[data-share-faq-status]');
    const pagination = section.querySelector('[data-share-faq-pagination]');
    const list = section.querySelector('[data-share-faq-list]');

    if (!items.length) {
      if (pagination) {
        pagination.style.display = 'none';
      }

      return;
    }

    const defaultOpen = Number(section.dataset.defaultOpen || 0);

    let currentPage = 0;
    let pageButtons = [];
    let itemsPerPage = getItemsPerPage(pagination);
    let totalPages = itemsPerPage > 0
      ? Math.ceil(items.length / itemsPerPage)
      : 1;

    const closeAllItems = () => {
      items.forEach(closeItem);
    };

    const toggleItem = (item) => {
      const isOpen = item.classList.contains('is-open');

      closeAllItems();

      if (!isOpen) {
        openItem(item);
      }
    };

    const scrollToFaqList = () => {
      if (!list) {
        return;
      }

      const top = list.getBoundingClientRect().top + window.scrollY - 110;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    };

    const updatePageButtons = () => {
      pageButtons.forEach((button, index) => {
        const isActive = index === currentPage;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'page' : 'false');
      });

      if (previousButton) {
        previousButton.disabled = currentPage === 0;
      }

      if (nextButton) {
        nextButton.disabled = currentPage === totalPages - 1;
      }

      if (status) {
        status.textContent = `${currentPage + 1} / ${totalPages}`;
      }
    };

    const renderPage = (pageIndex, shouldScroll = false) => {
      currentPage = Math.max(0, Math.min(pageIndex, totalPages - 1));

      closeAllItems();

      if (itemsPerPage <= 0) {
        items.forEach((item) => {
          item.classList.add('is-page-visible');
        });
      } else {
        const start = currentPage * itemsPerPage;
        const end = start + itemsPerPage;

        items.forEach((item, index) => {
          item.classList.toggle(
            'is-page-visible',
            index >= start && index < end
          );
        });
      }

      updatePageButtons();

      if (shouldScroll) {
        scrollToFaqList();
      }
    };

    const buildPagination = () => {
      if (!pagination || !pagesContainer || totalPages <= 1) {
        if (pagination) {
          pagination.style.display = 'none';
        }

        pageButtons = [];
        return;
      }

      pagination.style.display = '';
      pagesContainer.innerHTML = '';
      pagesContainer.style.display = 'flex';
      pagesContainer.style.gap = '6px';

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'share-faq-page-number';
        button.textContent = String(pageIndex + 1).padStart(2, '0');
        button.setAttribute(
          'aria-label',
          `Show FAQ page ${pageIndex + 1}`
        );

        button.addEventListener('click', () => {
          renderPage(pageIndex, true);
        });

        pagesContainer.appendChild(button);
      }

      pageButtons = Array.from(
        pagesContainer.querySelectorAll('.share-faq-page-number')
      );
    };

    const refreshPagination = () => {
      const previousItemsPerPage = itemsPerPage;
      itemsPerPage = getItemsPerPage(pagination);
      totalPages = itemsPerPage > 0
        ? Math.ceil(items.length / itemsPerPage)
        : 1;

      if (previousItemsPerPage !== itemsPerPage) {
        currentPage = 0;
      }

      buildPagination();
      renderPage(currentPage, false);
    };

    items.forEach((item) => {
      const button = item.querySelector('[data-share-faq-button]');

      button?.addEventListener('click', () => {
        toggleItem(item);
      });
    });

    previousButton?.addEventListener('click', () => {
      renderPage(currentPage - 1, true);
    });

    nextButton?.addEventListener('click', () => {
      renderPage(currentPage + 1, true);
    });

    section.addEventListener('shareFaq:refresh', refreshPagination);

    refreshPagination();

    if (defaultOpen > 0) {
      const defaultIndex = defaultOpen - 1;
      const defaultItem = items[defaultIndex];

      if (defaultItem) {
        if (itemsPerPage > 0) {
          currentPage = Math.floor(defaultIndex / itemsPerPage);
          buildPagination();
          renderPage(currentPage, false);
        }

        openItem(defaultItem);
      }
    }
  };

  const initAllShareFaqSections = () => {
    document.querySelectorAll(sectionSelector).forEach(initShareFaqSection);
  };

  const refreshAllShareFaqSections = () => {
    document.querySelectorAll(sectionSelector).forEach((section) => {
      section.dispatchEvent(new CustomEvent('shareFaq:refresh'));
    });
  };

  window.addEventListener(
    'resize',
    () => {
      refreshAllShareFaqSections();

      document.querySelectorAll(`${sectionSelector} .share-faq-item.is-open`)
        .forEach((item) => {
          const answer = item.querySelector('[data-share-faq-answer]');

          if (answer) {
            answer.style.height = `${answer.scrollHeight}px`;
          }
        });
    },
    { passive: true }
  );

  document.addEventListener('DOMContentLoaded', initAllShareFaqSections);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);

    if (section) {
      initShareFaqSection(section);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest?.('[data-share-faq-item]');

    if (!item) {
      return;
    }

    const section = item.closest(sectionSelector);

    if (!section) {
      return;
    }

    const items = Array.from(section.querySelectorAll('[data-share-faq-item]'));
    const index = items.indexOf(item);
    const pagination = section.querySelector('[data-share-faq-pagination]');
    const itemsPerPage = getItemsPerPage(pagination);

    if (itemsPerPage > 0 && index >= 0) {
      const targetPage = Math.floor(index / itemsPerPage);
      const pageButton = section.querySelectorAll('.share-faq-page-number')[targetPage];

      pageButton?.click();
    }

    section.querySelectorAll('[data-share-faq-item]').forEach(closeItem);
    openItem(item);
  });

  if (document.readyState !== 'loading') {
    initAllShareFaqSections();
  }
})();